import { Link } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import logoDark from '../assets/logo-repro-v3-dark.svg';
import logoWhite from '../assets/logo-repro-white.svg';
import logoIconDark from '../assets/logo-repro-v3-dark.svg';
import logoIconWhite from '../assets/logo-repro-white.svg';

interface HeaderProps {
  /** Current org name to show in the selector. Pass null/undefined for single-org users to hide the selector. */
  currentOrgName?: string | null;
  /** Total number of orgs the user has access to. Drives whether the chevron + selector renders. */
  orgCount?: number;
  /** Click handler for the org selector. */
  onOrgSelect?: () => void;
  /** Email shown in the user-menu trigger. */
  userEmail: string;
  /** Click handler for the user-menu avatar. Opens the UserMenuDrawer. */
  onUserMenu: () => void;
}

/**
 * Top-bar shared across every authenticated customer-portal page. Logo on
 * the left, optional org selector on the right, user-menu trigger on the
 * far right.
 *
 * Per the v2 design (Customer Portal v2 / Header/v2 in the .pen file).
 * Q4 in the migration map: one shared header, used everywhere.
 */
export default function Header({
  currentOrgName,
  orgCount = 1,
  onOrgSelect,
  userEmail,
  onUserMenu,
}: HeaderProps) {
  const { resolvedTheme } = useTheme();
  const logo = resolvedTheme === 'dark' ? logoWhite : logoDark;
  const icon = resolvedTheme === 'dark' ? logoIconWhite : logoIconDark;

  return (
    <header className="flex h-14 items-center justify-between border-b border-base-300 bg-base-100 px-6">
      <Link
        to="/dashboard"
        className="flex items-center"
        aria-label="RePro — go to dashboard"
      >
        <img src={logo} alt="RePro" className="h-6 w-auto" />
      </Link>

      <div className="flex items-center gap-3">
        {orgCount > 1 && currentOrgName && (
          <button
            type="button"
            onClick={onOrgSelect}
            className="flex items-center gap-2 rounded-btn border border-base-300 bg-base-200 px-3 py-1.5 text-sm font-medium text-base-content hover:bg-base-300/50"
            aria-haspopup="menu"
          >
            <span>{currentOrgName}</span>
            <span className="text-xs" aria-hidden="true">
              ▼
            </span>
          </button>
        )}

        <button
          type="button"
          onClick={onUserMenu}
          className="flex items-center gap-2 rounded-btn px-2 py-1 hover:bg-base-200"
          aria-haspopup="dialog"
          aria-label="Open account menu"
        >
          <span className="text-sm text-base-content">{userEmail}</span>
          <span
            className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-base-300 bg-base-200"
            aria-hidden="true"
          >
            <img src={icon} alt="" className="h-3.5 w-auto" />
          </span>
          <span className="text-xs text-base-content" aria-hidden="true">
            ▼
          </span>
        </button>
      </div>
    </header>
  );
}
